import type { Nullable } from '~/types'
import type { PinturaEditorHistoryAPI, PinturaNode, Size } from '@pqina/pintura'

type CropRect = {
  x: number
  y: number
  width: number
  height: number
}

export type PinturaEditorWithCropStores = {
  imageRotation?: number
  imageCropAspectRatio?: number
  imageCropMinSize?: Size
  imageSize?: Size
  imageCrop?: CropRect
  history?: PinturaEditorHistoryAPI
  on?: (event: string, callback: () => void) => void
}

const HALF_PI = Math.PI / 2

// Relaxes Pintura `imageCropMinSize` / `rotationRange` checks during a controlled rotate.
const PINTURA_CROP_MIN_SIZE_BYPASS: Size = { width: 1, height: 1 }

// JPEG rounding can shave a pixel off exported crop dimensions.
const GRANDFATHERED_OUTPUT_DIMENSION_TOLERANCE_PX = 2

/**
 * Converts rotation radians to quarter turns.
 *
 * @param rotationRadians The rotation radians to convert to quarter turns.
 * @returns The quarter turns.
 */
export function rotationRadiansToQuarterTurns(rotationRadians: number): number {
  return ((Math.round(rotationRadians / HALF_PI) % 4) + 4) % 4
}

/**
 * Converts quarter turns to radians.
 *
 * @param quarterTurns The quarter turns to convert to radians.
 * @returns The radians.
 */
export function quarterTurnsToRadians(quarterTurns: number): number {
  const normalized = ((quarterTurns % 4) + 4) % 4

  if (normalized === 1) {
    return HALF_PI
  }

  if (normalized === 2) {
    return Math.PI
  }

  if (normalized === 3) {
    return -HALF_PI
  }

  return 0
}

/**
 * Gets the next quarter turns after a rotate.
 *
 * @param currentRotationRadians The current rotation radians.
 * @param deltaRadians The delta radians to get the next quarter turns after a rotate for.
 * @returns The next quarter turns after a rotate.
 */
export function getNextQuarterTurnsAfterRotate(
  currentRotationRadians: number,
  deltaRadians: number,
): number {
  const currentQuarterTurns = rotationRadiansToQuarterTurns(currentRotationRadians)
  const deltaQuarterTurns = deltaRadians < 0 ? -1 : 1

  return (currentQuarterTurns + deltaQuarterTurns + 4) % 4
}

/**
 * Numeric rotation in radians; non-numbers (e.g. Pintura stores) are treated as 0.
 *
 * @param editor The editor to resolve the rotation radians for.
 * @returns The rotation radians.
 *
 * @example resolveEditorRotationRadians({ imageRotation: 1.5 }) // 1.5
 * @example resolveEditorRotationRadians({ imageRotation: { subscribe: () => () => {} } }) // 0
 */
export function resolveEditorRotationRadians(editor: PinturaEditorWithCropStores): number {
  const rotation = editor.imageRotation

  return typeof rotation === 'number' ? rotation : 0
}

/**
 * Image bounds after a 90° rotation (width and height swap on odd quarter turns).
 *
 * @param imageWidth The width of the image.
 * @param imageHeight The height of the image.
 * @param quarterTurns The quarter turns of the editor rotation.
 * @returns The rotated image bounds.
 */
export function getRotatedImageBounds(
  imageWidth: number,
  imageHeight: number,
  quarterTurns: number,
): Size {
  if (quarterTurns % 2 === 1) {
    return { width: imageHeight, height: imageWidth }
  }

  return { width: imageWidth, height: imageHeight }
}

/**
 * Full-frame crop rect for the current rotation (origin at 0,0).
 *
 * @param imageWidth The width of the image.
 * @param imageHeight The height of the image.
 * @param quarterTurns The quarter turns of the editor rotation.
 * @returns The full-frame crop rect.
 */
export function getFullImageCropRect(
  imageWidth: number,
  imageHeight: number,
  quarterTurns: number,
): CropRect {
  const bounds = getRotatedImageBounds(imageWidth, imageHeight, quarterTurns)

  return {
    x: 0,
    y: 0,
    width: bounds.width,
    height: bounds.height,
  }
}

/**
 * Grandfathered photos: minimum crop equals the full image at the current rotation.
 * Prevents zooming/cropping below the loaded image resolution.
 *
 * @param imageWidth The width of the image.
 * @param imageHeight The height of the image.
 * @param quarterTurns The quarter turns to resolve the grandfathered photo crop min size for.
 * @returns The grandfathered photo crop min size.
 *
 * @example resolveGrandfatheredPhotoCropMinSize(400, 300, 0) // { width: 400, height: 300 }
 * @example resolveGrandfatheredPhotoCropMinSize(400, 300, 1) // { width: 300, height: 400 }
 */
export function resolveGrandfatheredPhotoCropMinSize(
  imageWidth: number,
  imageHeight: number,
  quarterTurns: number,
): Size {
  return getRotatedImageBounds(imageWidth, imageHeight, quarterTurns)
}

/**
 * True when processed output is at least as large as the source on both long and short edges
 * (rotation may swap width and height). Allows a small JPEG rounding tolerance.
 *
 * @param outputWidth The width of the output.
 * @param outputHeight The height of the output.
 * @param sourceWidth The width of the source.
 * @param sourceHeight The height of the source.
 * @returns True when processed output preserves source resolution.
 *
 * @example processedGrandfatheredOutputPreservesSource(400, 300, 400, 300) // true
 * @example processedGrandfatheredOutputPreservesSource(300, 400, 400, 300) // true
 * @example processedGrandfatheredOutputPreservesSource(30, 22, 400, 300) // false
 */
export function processedGrandfatheredOutputPreservesSource(
  outputWidth: number,
  outputHeight: number,
  sourceWidth: number,
  sourceHeight: number,
): boolean {
  const tolerance = GRANDFATHERED_OUTPUT_DIMENSION_TOLERANCE_PX
  const outputLong = Math.max(outputWidth, outputHeight)
  const outputShort = Math.min(outputWidth, outputHeight)
  const sourceLong = Math.max(sourceWidth, sourceHeight)
  const sourceShort = Math.min(sourceWidth, sourceHeight)

  return outputLong + tolerance >= sourceLong && outputShort + tolerance >= sourceShort
}

/**
 * Restores the grandfathered photo crop restrictions to the full image frame.
 *
 * @param editor The editor to restore the grandfathered photo crop restrictions for.
 * @param imageWidth The width of the image.
 * @param imageHeight The height of the image.
 * @param quarterTurns The quarter turns to restore the grandfathered photo crop restrictions for.
 */
function restoreGrandfatheredPhotoCropRestrictions(
  editor: PinturaEditorWithCropStores,
  imageWidth: number,
  imageHeight: number,
  quarterTurns: number,
): void {
  editor.imageCropMinSize = resolveGrandfatheredPhotoCropMinSize(
    imageWidth,
    imageHeight,
    quarterTurns,
  )
  editor.imageCrop = getFullImageCropRect(imageWidth, imageHeight, quarterTurns)
}

/**
 * Rotate ±90° for grandfathered photos and keep the crop on the full image frame.
 *
 * @param editor The editor to apply the grandfathered photo rotate to.
 * @param deltaRadians The delta radians to apply the grandfathered photo rotate for.
 */
export function applyGrandfatheredPhotoRotate(
  editor: PinturaEditorWithCropStores,
  deltaRadians: number,
): void {
  const imageSize = editor.imageSize
  if (!imageSize?.width || !imageSize?.height) {
    return
  }

  const { width: imageWidth, height: imageHeight } = imageSize
  const newQuarterTurns = getNextQuarterTurnsAfterRotate(
    resolveEditorRotationRadians(editor),
    deltaRadians,
  )
  const newRotation = quarterTurnsToRadians(newQuarterTurns)

  editor.imageCropMinSize = PINTURA_CROP_MIN_SIZE_BYPASS
  editor.imageCropAspectRatio = undefined
  editor.imageRotation = newRotation
  restoreGrandfatheredPhotoCropRestrictions(
    editor,
    imageWidth,
    imageHeight,
    newQuarterTurns,
  )

  editor.history?.write()
}

/**
 * Whether the tool is a rotate crop tool node.
 *
 * @param tool The tool to check if it is a rotate crop tool node.
 * @returns True when the tool is a rotate crop tool node.
 */
function isRotateCropToolNode(tool: unknown): tool is ['Button', 'rotate-left' | 'rotate-right', Record<string, unknown>] {
  return Array.isArray(tool)
    && tool.length >= 3
    && tool[0] === 'Button'
    && (tool[1] === 'rotate-left' || tool[1] === 'rotate-right')
}

/**
 * Maps rotate buttons to full-frame grandfathered rotate handlers.
 *
 * @param tools The tools to map.
 * @param getEditor The get editor function.
 * @param refreshCropToolbar Re-runs Pintura crop toolbar mapping after rotation.
 * @returns The mapped crop tools.
 */
export function mapGrandfatheredPhotoCropTools(
  tools: PinturaNode[],
  getEditor: () => Nullable<PinturaEditorWithCropStores>,
  refreshCropToolbar?: () => void,
): PinturaNode[] {
  return tools.map((tool) => {
    if (!isRotateCropToolNode(tool)) {
      return tool
    }

    const [, id, props] = tool
    const deltaRadians = id === 'rotate-left' ? -HALF_PI : HALF_PI

    return [
      tool[0],
      id,
      {
        ...props,
        onclick: () => {
          const editor = getEditor()
          if (!editor) {
            return
          }

          applyGrandfatheredPhotoRotate(editor, deltaRadians)
          refreshCropToolbar?.()
        },
      },
    ]
  })
}

type CropToolbarEditorBinding = {
  bindEditor: (editor: PinturaEditorWithCropStores) => void
  wrapCropWillRenderTools: (
    mapTools: (
      tools: PinturaNode[],
      getEditor: () => Nullable<PinturaEditorWithCropStores>,
      refreshCropToolbar: () => void,
    ) => PinturaNode[],
  ) => (tools: PinturaNode[], env?: unknown, redraw?: () => void) => PinturaNode[]
}

/**
 * Tracks the bound editor and Pintura crop-toolbar redraw so rotate handlers can refresh
 * after bind or rotation.
 *
 * @returns Editor binding helpers for cropWillRenderTools and bindEditor.
 */
function createCropToolbarEditorBinding(): CropToolbarEditorBinding {
  const editorRef: { current: Nullable<PinturaEditorWithCropStores> } = { current: null }
  let cropToolbarRedraw: (() => void) | undefined

  const refreshCropToolbar = () => {
    cropToolbarRedraw?.()
  }

  return {
    bindEditor: (editor: PinturaEditorWithCropStores) => {
      editorRef.current = editor
      refreshCropToolbar()
    },
    wrapCropWillRenderTools: (mapTools) => (tools, _env, redraw) => {
      cropToolbarRedraw = redraw
      return mapTools(tools, () => editorRef.current, refreshCropToolbar)
    },
  }
}

/**
 * Enhances the editor options for grandfathered photos.
 *
 * @param editorOptions The editor options to enhance.
 * @returns The enhanced editor options for grandfathered photos.
 */
export function enhanceEditorOptionsForGrandfatheredPhotos<T extends Record<string, unknown>>(
  editorOptions: T,
): {
  editorOptions: T & {
    cropEnableButtonRotateLeft: true
    cropEnableButtonRotateRight: true
    cropEnableRotationInput: false
    cropWillRenderTools: (tools: PinturaNode[], env?: unknown, redraw?: () => void) => PinturaNode[]
  }
  bindEditor: (editor: PinturaEditorWithCropStores) => void
} {
  const binding = createCropToolbarEditorBinding()

  return {
    editorOptions: {
      ...editorOptions,
      cropEnableButtonRotateLeft: true,
      cropEnableButtonRotateRight: true,
      cropEnableRotationInput: false,
      cropWillRenderTools: binding.wrapCropWillRenderTools((tools, getEditor, refreshCropToolbar) =>
        mapGrandfatheredPhotoCropTools(tools, getEditor, refreshCropToolbar),
      ),
    },
    bindEditor: binding.bindEditor,
  }
}

/**
 * Snaps the grandfathered photo editor rotation and crop to the full image frame.
 *
 * @param editor The editor to snap the grandfathered photo editor rotation and crop for.
 */
export function snapGrandfatheredPhotoEditorRotationAndCrop(
  editor: PinturaEditorWithCropStores,
): void {
  const imageSize = editor.imageSize
  if (!imageSize?.width || !imageSize?.height) {
    return
  }

  const { width: imageWidth, height: imageHeight } = imageSize
  const quarterTurns = rotationRadiansToQuarterTurns(resolveEditorRotationRadians(editor))
  const snappedRotation = quarterTurnsToRadians(quarterTurns)

  editor.imageCropMinSize = PINTURA_CROP_MIN_SIZE_BYPASS
  editor.imageCropAspectRatio = undefined
  editor.imageRotation = snappedRotation
  restoreGrandfatheredPhotoCropRestrictions(editor, imageWidth, imageHeight, quarterTurns)
}

/**
 * Attaches the Pintura grandfathered photo editor hooks.
 *
 * @param editor The editor to attach the Pintura grandfathered photo editor hooks to.
 */
export function attachPinturaGrandfatheredPhotoEditorHooks(
  editor: PinturaEditorWithCropStores,
): void {
  editor.on?.('load', () => {
    snapGrandfatheredPhotoEditorRotationAndCrop(editor)
  })
}
